import React, { useRef, useEffect, useState, useCallback, useId } from 'react';

const PHONE_RE = /^\+?[0-9\s\-()]{7,20}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Renders a single line of bot text with minimal, safe formatting:
 * **bold** segments and "* " / "- " bullets (no HTML injection).
 */
function renderLine(line, keyPrefix) {
  let bulletLine = line.replace(/^(\s*)[*-]\s+/, '$1• ');
  // While an answer is still streaming, a "**" may not have its closing pair yet: hide it
  const markers = bulletLine.match(/\*\*/g);
  if (markers && markers.length % 2 === 1) {
    const idx = bulletLine.lastIndexOf('**');
    bulletLine = bulletLine.slice(0, idx) + bulletLine.slice(idx + 2);
  }
  return bulletLine.split(/(\*\*[^*\n]+\*\*)/g).map((part, i) => {
    if (/^\*\*[^*\n]+\*\*$/.test(part)) {
      return <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={`${keyPrefix}-${i}`}>{part}</React.Fragment>;
  });
}

export default function ChatMessages({
  messages,
  isTyping,
  isResponding,
  onQuickActionClick,
  onSubmitEscalation,
  isSubmittingEscalation,
  onTypingComplete,
}) {
  const scrollRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  return (
    <div className="msq-messages-area" ref={scrollRef} role="log" aria-live="polite" aria-relevant="additions">
      {messages.map((msg, index) => (
        <MessageRow
          key={msg.id || index}
          msg={msg}
          onQuickActionClick={onQuickActionClick}
          onSubmitEscalation={onSubmitEscalation}
          isSubmittingEscalation={isSubmittingEscalation}
          onTypingComplete={onTypingComplete}
          onScrollRequested={scrollToBottom}
          quickActionsDisabled={isTyping || isResponding}
        />
      ))}

      {/* Dynamic Analyzing & Formulating indicator */}
      {isTyping && (
        <div className="msq-message-row bot">
          <div className="msq-thinking-indicator">
            <span className="msq-thinking-sparkle">✨</span>
            <div className="msq-thinking-text-group">
              <span className="msq-thinking-title">MSquare Assistant is analysing your question...</span>
              <span className="msq-thinking-sub">Preparing answer in real-time</span>
            </div>
            <div className="msq-typing-indicator ms-2 py-1 px-2 border-0 bg-transparent shadow-none">
              <span className="msq-typing-dot"></span>
              <span className="msq-typing-dot"></span>
              <span className="msq-typing-dot"></span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MessageRow({
  msg,
  onQuickActionClick,
  onSubmitEscalation,
  isSubmittingEscalation,
  onTypingComplete,
  onScrollRequested,
  quickActionsDisabled,
}) {
  const [displayedText, setDisplayedText] = useState(msg.isLiveTyping ? '' : msg.text);
  const [isDoneTyping, setIsDoneTyping] = useState(!msg.isLiveTyping);

  useEffect(() => {
    if (!msg.isLiveTyping) {
      setDisplayedText(msg.text);
      setIsDoneTyping(true);
      return;
    }

    const fullText = msg.text || '';
    const words = fullText.match(/\S+\s*/g) || [fullText];
    let currentIndex = 0;
    let accumulated = '';

    const interval = setInterval(() => {
      if (currentIndex < words.length) {
        // Fast reveal (~5 words per frame) so instant answers still feel live but never slow
        const step = words.slice(currentIndex, currentIndex + 5).join('');
        accumulated += step;
        currentIndex += 5;
        setDisplayedText(accumulated);
        if (onScrollRequested) onScrollRequested();
      } else {
        clearInterval(interval);
        setDisplayedText(fullText);
        setIsDoneTyping(true);
        if (onTypingComplete) onTypingComplete(msg.id);
        if (onScrollRequested) onScrollRequested();
      }
    }, 16);

    return () => clearInterval(interval);
  }, [msg.id, msg.isLiveTyping, msg.text, onTypingComplete, onScrollRequested]);

  return (
    <div className={`msq-message-row ${msg.sender}`}>
      <div className="msq-bubble">
        {(msg.isLiveTyping ? displayedText : (msg.text || '')).split('\n').map((line, lIdx, lines) => (
          <React.Fragment key={lIdx}>
            {msg.sender === 'bot' ? renderLine(line, lIdx) : line}
            {lIdx < lines.length - 1 && <br />}
          </React.Fragment>
        ))}

        {(!isDoneTyping || msg.isStreaming) && <span className="msq-cursor-blink" title="Generating..."></span>}

        {isDoneTyping && !msg.isStreaming && msg.showEscalationForm && !msg.escalationSubmitted && (
          <div className="msq-fade-in">
            <EscalationForm
              originalQuestion={msg.originalQuestion || ''}
              onSubmit={(data) => onSubmitEscalation(msg.id, data)}
              isSubmitting={isSubmittingEscalation}
              serverError={msg.escalationError || ''}
            />
          </div>
        )}


      </div>

      <div className="msq-message-time">{msg.time}</div>

      {msg.showWelcomePrompts && (
        <div className="msq-quick-actions msq-fade-in mt-2">
          <button
            type="button"
            className="msq-quick-chip"
            disabled={quickActionsDisabled}
            onClick={() => onQuickActionClick('What does MSquare actually do?')}
          >
            🚀 What does MSquare do?
          </button>
          <button
            type="button"
            className="msq-quick-chip"
            disabled={quickActionsDisabled}
            onClick={() => onQuickActionClick('How can you help automate our business workflows and CRM?')}
          >
            ⚡ Business & CRM Automation
          </button>
          <button
            type="button"
            className="msq-quick-chip"
            disabled={quickActionsDisabled}
            onClick={() => onQuickActionClick('What custom software, web & mobile apps do you build?')}
          >
            💻 Custom Software & Apps
          </button>
          <button
            type="button"
            className="msq-quick-chip"
            disabled={quickActionsDisabled}
            onClick={() => onQuickActionClick('How do you help with Growth Marketing & Local SEO?')}
          >
            📈 Growth Marketing & SEO
          </button>
        </div>
      )}

      {isDoneTyping && msg.showHumanAssistancePrompt && !msg.showEscalationForm && !msg.escalationSubmitted && (
        <div className="msq-quick-actions msq-fade-in">
          <button
            type="button"
            className="msq-quick-chip btn-primary text-primary"
            onClick={() => onQuickActionClick('Talk to Our Team', msg.originalQuestion)}
          >
            Yes, connect me with support
          </button>
          <button
            type="button"
            className="msq-quick-chip text-secondary"
            onClick={() => onQuickActionClick('Explore Our Services')}
          >
            Explore other topics
          </button>
        </div>
      )}
    </div>
  );
}

function EscalationForm({ originalQuestion, onSubmit, isSubmitting, serverError }) {
  const consentId = useId();
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (!PHONE_RE.test(mobile.trim())) {
      setError('Please provide a valid mobile number.');
      return;
    }
    if (!EMAIL_RE.test(email.trim())) {
      setError('Please provide a valid email address.');
      return;
    }
    if (!consent) {
      setError('Please check the consent box to proceed.');
      return;
    }

    setError('');
    onSubmit({
      name: name.trim(),
      mobile: mobile.trim(),
      email: email.trim(),
      question: originalQuestion,
      consent,
    });
  };

  return (
    <form className="msq-escalation-card" onSubmit={handleSubmit}>
      <h6 className="d-flex align-items-center gap-2">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="var(--brand-orange)">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
        <span>Connect with Our Team</span>
      </h6>
      <p className="msq-escalation-sub mb-2">
        Share your details and an MSquare consultant will get in touch with verified information.
      </p>

      {(error || serverError) && (
        <div className="alert alert-danger py-1 px-2 mb-2 small" role="alert">{error || serverError}</div>
      )}

      <div className="mb-2">
        <input
          type="text"
          className="form-control msq-form-input"
          placeholder="Your Full Name"
          aria-label="Your full name"
          autoComplete="name"
          maxLength={100}
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isSubmitting}
        />
      </div>

      <div className="mb-2">
        <input
          type="tel"
          className="form-control msq-form-input"
          placeholder="Mobile Number (e.g. 9876543210)"
          aria-label="Mobile number"
          autoComplete="tel"
          maxLength={20}
          value={mobile}
          onChange={(e) => setMobile(e.target.value)}
          disabled={isSubmitting}
        />
      </div>

      <div className="mb-2">
        <input
          type="email"
          className="form-control msq-form-input"
          placeholder="Email Address"
          aria-label="Email address"
          autoComplete="email"
          maxLength={254}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isSubmitting}
        />
      </div>

      <div className="form-check mb-2">
        <input
          className="form-check-input msq-consent-check"
          type="checkbox"
          id={consentId}
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          disabled={isSubmitting}
        />
        <label className="form-check-label msq-consent-label" htmlFor={consentId}>
          I consent to an MSquare representative contacting me.
        </label>
      </div>

      <button
        type="submit"
        className="msq-btn-submit w-100"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Sending Request...' : 'Submit to Team'}
      </button>
    </form>
  );
}
